import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { SiriProviderShellComponent } from './siri-provider-shell.component';
import { ProviderDetailsLayoutComponent } from '../shared/provider-details-layout.component';
import { FeedDiagnostics, SiriDiagnosticsPayload } from '../../../shared/models/feed-diagnostics.model';
import { deriveFeedHealth } from '../util/feed-diagnostics.util';
import { relativeTime } from '../../../shared/utils/relative-time';
import { StackedBar, StackedBarSegment } from '../../../shared/components/stacked-bar/stacked-bar';
import { LanguageService } from '../../../core/i18n/language.service';

/**
 * SIRI Details tab (/admin/providers/siri/:providerId/details): the shared
 * provider Details layout plus a Stop health card — total stops, OK/Error/
 * Unknown counts, a stacked bar and snapshot ages, rolled up from every feed's
 * latest snapshot in the SIRI shell's diagnostics map.
 */
@Component({
  selector: 'app-siri-provider-details',
  standalone: true,
  imports: [TranslocoDirective, ProviderDetailsLayoutComponent, StackedBar],
  templateUrl: './siri-provider-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full min-h-0' },
})
export class SiriProviderDetailsComponent {
  private readonly shell = inject(SiriProviderShellComponent);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);

  readonly isDiagnosticsLoading = this.shell.isDiagnosticsLoading;

  /** Stop-level rollup across every feed's latest snapshot. Feeds with no
   *  snapshot are counted separately (noSnapshotCount) rather than folded
   *  into the 0/0/0 totals. */
  readonly stopHealthTotals = computed(() => {
    const diagnostics = this.shell.diagnostics();
    let total = 0;
    let ok = 0;
    let error = 0;
    let unknown = 0;
    let noSnapshotCount = 0;
    let newestGeneratedAt: string | null = null;
    let oldestGeneratedAt: string | null = null;
    for (const feed of this.shell.feeds()) {
      const snapshot = diagnostics.get(feed.id) as FeedDiagnostics<SiriDiagnosticsPayload> | undefined;
      if (!snapshot) {
        noSnapshotCount++;
        continue;
      }
      const health = deriveFeedHealth(snapshot);
      total += health.total;
      ok += health.ok;
      error += health.error;
      unknown += health.unknown;
      if (!newestGeneratedAt || snapshot.generatedAt > newestGeneratedAt) newestGeneratedAt = snapshot.generatedAt;
      if (!oldestGeneratedAt || snapshot.generatedAt < oldestGeneratedAt) oldestGeneratedAt = snapshot.generatedAt;
    }
    return {
      total,
      ok,
      error,
      unknown,
      noSnapshotCount,
      okPct: total ? (ok / total) * 100 : 0,
      errorPct: total ? (error / total) * 100 : 0,
      unknownPct: total ? (unknown / total) * 100 : 0,
      newestAge: newestGeneratedAt ? relativeTime(newestGeneratedAt) : null,
      oldestAge: oldestGeneratedAt ? relativeTime(oldestGeneratedAt) : null,
    };
  });

  /** OK / Error / Unknown for the bar (its own legend is off — the count row
   *  above already is the legend). Unknown is orange, as on the power bar. */
  readonly stopSegments = computed<StackedBarSegment[]>(() => {
    this.languageService.current();
    const totals = this.stopHealthTotals();
    const t = (k: string) => this.transloco.translate(`providers.detailPage.stopHealth.${k}`);
    return [
      { label: t('ok'), count: totals.ok, tone: 'ok' },
      { label: t('error'), count: totals.error, tone: 'error' },
      { label: t('unknown'), count: totals.unknown, tone: 'warn' },
    ];
  });
}
